import { ResidentStatus } from '@/enums/ResidentStatus';
import { ValidationRules } from '@/utils/validation-rules';
import firebase from "firebase/app";
import moment from 'moment';
import { getRegionName } from "@/utils/functionsRegion";
import { ResidentParams } from "@/consts/ResidentParams";
import exportFromJSON from 'export-from-json';
import { residentsFileHeaders } from '@/utils/importHalper';
import { fixString } from "@/utils/fixString";
import { EventLocations } from "@/enums/EventLocations";
import { FamilyHubAppStatus } from "@/enums/FamilyHubAppStatus";

export default {
  name: 'ResidentList',
  components: {
    AdmitResidentsDialog: () => import('../admitResidentsDialog/AdmitResidentsDialog.vue')
  },
  data() {
    return {
      admitStaffAccess: true,
      admitName: '',
      admitDoB: moment(new Date()).subtract(1, 'days').format('YYYY-MM-DD'),
      admitRoom: '',
      admitWing: '',
      admitPreferredName: "",
      showDepartedResidents: false,
      minDate: moment(new Date()).format('YYYY-MM-DD'),
      isManual: true,
      admitResidentsDialogShow: false,
      headers:[],
      search: '',
      ResidentStatus: ResidentStatus,
      sortBy: "isUsingMyHub",
      sortDesc: true,
      loading: true,
      onResidentsUnsubscribe: null,
      residentsDeparted: [],
      residentsReloaded: false,
      isExporting: false,
      selectedWing: null,
    };
  },
  watch: {
    async showDepartedResidents() {
      this.loading = true;
      await this.getResidents();
      this.loading = false;
    },
  },
  computed: {
    rules() {
      return {
        name: [ValidationRules.required, ValidationRules.max(70)],
        preferredName: [ValidationRules.max(70)],
        room: [ValidationRules.required],
        wing: [ValidationRules.required],
      };
    },
    residents() {
      return !this.showDepartedResidents ? this.$store.state.residentModule.residentsShortData.filter(x => x.status !== ResidentStatus.Departed) : this.residentsDeparted;
    },
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    status() {
      return this.showDepartedResidents ? ResidentStatus.Departed : ResidentStatus.ActiveResident;
    },
    filteredResidents() {
      const search = this.search.toLowerCase();

      let residents = this.residents.filter(
        resident => 
          resident.name && resident.name.toLowerCase().indexOf(search) >= 0 ||
          resident.wingName && resident.wingName.toLowerCase().indexOf(search) >= 0 ||
          resident.roomName && resident.roomName.toLowerCase().indexOf(search) >= 0 ||
          resident.pname && resident.pname.toLowerCase().indexOf(search) >= 0
      );

      if (this.selectedWing) {
        residents = residents.filter(resident => resident.wingName === this.selectedWing);
      }

      return residents;
    },
    contentCategories() {
      return this.$store.state.contentLibraryModule.contentCategories;
    },
    wings() {
      return this.$store.state.eventLocationModule.eventLocations.filter(x => x.type === EventLocations.Wing);
    },
    residentsTableText() {
      if(this.selectedWing && !this.filteredResidents.length) {
        return this.$t('ResidentsList.NoDataByWing');
      } else {
        return this.$t('ResidentsList.NoData');
      }
    }
  },
  created(){
    this.headers = [
      {
        sortable: false,
        width: 70,
        value: 'imageUrl',
        show: true
      },
      {
        text: this.$i18n.messages[this.$i18n.locale]['ResidentsList.Name'],
        align: 'start',
        sortable: true,
        value: 'name',
        show: true
      },
      {
        text: this.$i18n.messages[this.$i18n.locale]['ResidentsList.PreferredName'],
        align: 'start',
        sortable: true,
        value: 'pname',
        show: true
      },
      {
        text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Wing'],
        value: 'wingName',
        show: true
      },
      {
        text: this.$i18n.messages[this.$i18n.locale]['GeneralTranslations.Room'],
        value: 'roomName',
        show: true
      },
      {
        text: this.$i18n.messages[this.$i18n.locale]['ResidentsList.CheckedIn'],
        value: 'checkInTime',
        width: 150,
        show: this.$store.state.facilityModule.selectedFacility?.dailyCheckInEnabled
      },
      {
        text: this.$i18n.messages[this.$i18n.locale]['ResidentsList.UsingMyHub'],
        value: 'isUsingMyHub',
        width: 150,
        show: true
      }
    ].filter(x=>x.show)
    
  },
  async mounted() {
    Promise.all([
      await this.$store.dispatch('getFacility'),
      this.$store.dispatch("getContentCategories"),
      this.$store.dispatch("getEventLocations"),
      this.getResidents(),
      this.checkResidentsFilterParams(),
    ]).then(() => {
      this.loading = false;
    });
  },
  methods: {
    async exportData() {
      this.isExporting = true
      let residents = this.$store.state.residentModule.residentsShortData

      //reload residents to get actual data
      if (!this.residentsReloaded) {
        residents = await this.$store.dispatch("getResidentsShortData")
        this.residentsReloaded = true
        console.log('residents reloaded: ' + residents.length)
      }

      const csvResidents = residents.map((resident) => {
        const residentData = {
          [residentsFileHeaders.facility]: this.facility.name,
          [residentsFileHeaders.residentName]: resident.name,
          [residentsFileHeaders.preferredName]: resident.pname,
          [residentsFileHeaders.residentCode]: resident.refId,
          [residentsFileHeaders.dob]: resident.dateOfBirth ? moment(resident.dateOfBirth._seconds * 1000).format('DD-MM-YYYY') : '',
          [residentsFileHeaders.admissionDate]: resident.createdDateTime ? moment(resident.createdDateTime._seconds * 1000).format('DD-MM-YYYY') : '',
          [residentsFileHeaders.isDeparted]: resident.status == ResidentStatus.Departed ? 'Y' : 'N',
          [residentsFileHeaders.discharged]: resident.departedTime ? moment(resident.departedTime._seconds * 1000).format('DD-MM-YYYY') : '',
          [residentsFileHeaders.bed]: resident.roomName,
          [residentsFileHeaders.wing]: resident.wingName,
          [residentsFileHeaders.isUsingOwnDevice]: resident.isUsingOwnDevice == true ? 'Y' : 'N',
          [residentsFileHeaders.email]: resident.email,
          [residentsFileHeaders.address]: resident.address
        }

        const familyMembers = this.$store.state.residentModule.familyMembers;
        let index = 0;

        familyMembers.forEach((familyMember) => {
          if(familyMember?.residentIds.includes(resident.id)) {
            index++;
            residentData[residentsFileHeaders.familyName.replace('[index]', `${index}`)] = familyMember?.name,
            residentData[residentsFileHeaders.familyEmail.replace('[index]', `${index}`)] = familyMember?.email,
            residentData[residentsFileHeaders.familyEpoa.replace('[index]', `${index}`)] = familyMember?.financialEPOA.find(x => x.id === resident.id)?.status,
            residentData[residentsFileHeaders.familyApp.replace('[index]', `${index}`)] = [FamilyHubAppStatus.Activated, FamilyHubAppStatus.ReadOnly].includes(familyMember?.appStatus),
            residentData[residentsFileHeaders.familyCanAdd.replace('[index]', `${index}`)] = familyMember?.isCanInvite
          }
        })

        return residentData;
      });

      try {
        exportFromJSON({ data: csvResidents, fileName: 'residents', exportType: exportFromJSON.types["csv"] });
      } catch (e) {
        throw new Error("Parsing failed!");
      } finally {
        this.isExporting = false
      }
    },

    handleClickResident(row): void {
      this.$router.push('/residents/' + row.id);
    },

    openAdmitResidentsDialog() {
      this.admitResidentsDialogShow = true
      if (this.$refs.admitResidentsDialogRef) this.$refs.admitResidentsDialogRef.open()
    },

    getCheckInLabelColor(checkInTime: firebase.firestore.Timestamp): string {
      const isSameDay = moment().isSame(
        moment(checkInTime?.seconds * 1000),
        'day'
      );

      const canBeCheckedInToday = moment().hours() < 18;

      if (isSameDay) {
        return 'green--text';
      } else if (canBeCheckedInToday) {
        return 'orange--text';
      }
      return 'red--text';
    },

    getCheckInStatus(checkInTime: firebase.firestore.Timestamp) {
      const isSameDay = moment().isSame(checkInTime?.seconds * 1000, 'day');

      if (isSameDay) {
        return `Checked-In ${moment(checkInTime?.seconds * 1000).format(
          'h:mm A'
        )}`;
      } else {
        return this.$t('ResidentsList.Pending');
      }
    },

    allowedDates(val) {
      return val < this.minDate;
    },

    async getResidents() {
      if (this.showDepartedResidents && !this.residentsDeparted.length) {
        const data = await firebase.app().functions(getRegionName()).httpsCallable("getResidentsShortData")({
          facilityId: this.facility.id,
          status: ResidentStatus.Departed,
          params: ResidentParams
        });
        this.residentsDeparted = Object.freeze(data.data.data.residents);
      }
    },
    onWingChange(wing){
      this.selectedWing = wing.name;
      this.$store.commit("setMainResidentsFilters", { wingName: this.selectedWing });
    },
    fixString(str, cut) {
      return fixString(str, 15, cut)
    },
    checkResidentsFilterParams() {
      this.selectedWing = this.$store.state.residentModule.mainResidentsFilters.wingName || this.selectedWing;
    }
  },
};
